import React from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import {
  AutocompleteContents,
  AutocompleteInfo,
  AutocompleteInput,
} from '../../../../components/CustomFields/SimpleAutocomplete';
import { WizardStep } from '../../../../components/Wizard/WizardStep';
import { InputComponentProps, OnSubmit, useWizard } from '../../../../design-system';
import { createSchema, FormFields } from '../../../../utils/formUtils';
import { ChargePointState, CommandsWizardState, CommandType } from '../types';

type CommandDetails = AutocompleteInfo<CommandType>;

type CommandStepState = { command: CommandType };

const commandOptions: CommandDetails[] = [
  { label: 'Change configuration', value: CommandType.CHANGE_CONFIGURATION },
  { label: 'Reset', value: CommandType.RESET },
  { label: 'Update firmware', value: CommandType.UPDATE_FIRMWARE },
  { label: 'Send data transfer', value: CommandType.DATA_TRANSFER },
];

const commandsFields: FormFields<CommandStepState> = {
  command: {
    id: 'command',
    label: 'Command',
    inputComponent: ({
      disabled,
      error,
      helperText,
      name,
      onBlur,
      onChange,
      value,
    }: InputComponentProps<CommandType>) => (
      <AutocompleteInput<CommandType>
        disabled={disabled}
        error={error}
        helperText={helperText ? 'Command is required' : undefined}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        options={commandOptions}
        useNullAsDefaultValue
        value={value}
      />
    ),
    valueComponent: ({ value: command }: AutocompleteContents<CommandDetails>) => <>{command.label}</>,
  },
};

const validationSchema = createSchema<CommandStepState>({ command: Yup.mixed<CommandType>().required() });

const getChargePointsMessage = (chargePointsState: ChargePointState[]): string => {
  const chargePoints = chargePointsState.map((c) => c.name ?? c.ocppChargePointId);
  if (chargePoints.length === 1) {
    return `Select the command to send to charge point (${chargePoints[0]})`;
  }
  if (chargePoints.length > 10) {
    return `Select the command to send to ${chargePoints.length} charge points`;
  }
  return `Select the command to send to ${chargePoints.length} charge points (${chargePoints.join(', ')})`;
};

export const CommandStep: React.FC = () => {
  const { state: routerState } = useLocation<Pick<CommandsWizardState, 'chargePoints'>>();

  const { state: untypedState, setState: untypedSetState, prevStep, nextStep, step } = useWizard();
  const state = untypedState as Partial<CommandsWizardState>;
  const setState = untypedSetState as (state: Partial<CommandsWizardState>) => void;

  const onCancel = React.useCallback<OnSubmit<CommandStepState>>(
    (values) => {
      setState({ command: { type: values.command } });
      prevStep();
    },
    [setState, prevStep],
  );

  const onSubmit = React.useCallback<OnSubmit<CommandStepState>>(
    (values, { setFieldError }) => {
      setFieldError('command', undefined);
      setState({ command: { type: values.command } });
      nextStep();
    },
    [setState, nextStep],
  );

  return (
    <WizardStep<CommandStepState>
      fields={Object.values(commandsFields)}
      initialValues={{ command: state.command?.type ?? CommandType.NO_COMMAND }}
      name="Select command"
      onCancel={onCancel}
      onSubmit={onSubmit}
      step={step}
      subtitle={getChargePointsMessage(routerState.chargePoints)}
      validationSchema={validationSchema}
    />
  );
};
